import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import Premium from '../Premium/Premium'
import Modal from './Modal'
import './_Subscriptions.scss'
import { useEffect, useMemo, useState } from 'react'
import { logGoogleAdsEvent } from '../../helpers/analytics'
import CheckoutForm, { Return } from '../Checkout'
import { FEATURE_FLAGS, usePricing } from '../../hooks/featureFlags'
import { useAuth } from '../../contexts/Auth'
import { CheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { LuLoaderCircle } from 'react-icons/lu'
import { useSelector } from 'react-redux'
import { selectUser } from '../../redux/systemSlice'

const stripePromise = loadStripe(
	'pk_live_51KXx04ApflHFLqu66s7RJuXDpQMqxcP4Hr3YmgRCFPP4Ylm7sNmWiP5mVddhxi2iqdSkvWkheM3zd6nJ6HhOrBaf00l05v3pXq'
)

function Subscriptions({
	open,
	setOpen,
	annualPlan,
	checkoutStep,
	setCheckoutStep,
	setLoginModal,
	noMoreCredits = false,
	onClose,
	unlockEssay,
}: {
	open: boolean
	setOpen: (open: boolean) => void
	annualPlan: boolean
	checkoutStep: 'selector' | 'checkout' | 'success'
	setCheckoutStep: (step: 'selector' | 'checkout' | 'success') => void
	setLoginModal: (open: boolean) => void
	noMoreCredits?: boolean
	onClose?: () => void
	unlockEssay?: boolean
}) {
	const posthog = usePostHog()
	const user = useSelector(selectUser)
	const { currentUser } = useAuth() as any
	const pricing = usePricing()

	useEffect(() => {
		if (open) {
			logGoogleAdsEvent('upgradeModal')
			posthog.capture('upgrade-modal-opened')
		}
	}, [open])

	// An async useMemo runs its body on render, and the Navbar mounts this modal
	// on every page, so this used to POST a checkout session on every page load.
	// `user` starts as NULL_USER ({ planId: null }), which is truthy but has no
	// id, and JSON.stringify drops undefined keys — so those calls reached the
	// backend with no userId and came back 400 "Missing required fields".
	const promise = useMemo(() => {
		if (!open || !user?.id) {
			return null
		}

		const createSession = async () => {
			const requestOptions = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
				},
				body: JSON.stringify({
					userId: user.id,
					humanizerUpgrade: false,
					price: annualPlan
						? pricing.stripePriceId.yearly ?? pricing.stripePriceId.monthly
						: pricing.stripePriceId.monthly,
					annual: annualPlan,
					embedded: true,
					returnUrl: window.location.href + '?checkout_complete=true&session_id={CHECKOUT_SESSION_ID}',
				}),
			}

			return fetch(process['env']['REACT_APP_API_ROOT'] + '/payments/checkout/', requestOptions)
				.then((res) => res.json())
				.then((data) => data.clientSecret)
		}

		return createSession()
	}, [currentUser, annualPlan, user, open])

	if (open && checkoutStep === 'checkout') {
		if (!promise) {
			return (
				<Modal
					open={open}
					closeModal={() => {
						setOpen(false)
						setCheckoutStep('selector')
						posthog.capture('upgrade-modal-closed')
					}}
					portalClassName="subscription v2"
				>
					<div className="flex items-center justify-center h-full min-h-[500px]">
						{LuLoaderCircle({ className: `animate-spin text-gray-500 w-8 h-8` })}
					</div>
				</Modal>
			)
		}
		return (
			<CheckoutProvider
				stripe={stripePromise}
				options={{
					fetchClientSecret: () => promise,
					elementsOptions: { appearance: { theme: 'stripe' } },
				}}
			>
				<CheckoutForm
					goBack={() => setCheckoutStep('selector')}
					onClose={() => {
						setOpen(false)
						setCheckoutStep('selector')
					}}
				/>
			</CheckoutProvider>
		)
	}

	if (open && checkoutStep === 'success') {
		return (
			<Return
				closeModal={() => {
					setOpen(false)
					setCheckoutStep('selector')
				}}
			/>
		)
	}

	return (
		<Modal
			open={open}
			closeModal={() => {
				setOpen(false)
				posthog.capture('upgrade-modal-closed')
				onClose?.()
			}}
			portalClassName="subscription v2"
		>
			{!promise && checkoutStep === 'checkout' ? (
				<div className="flex items-center justify-center h-full min-h-[500px]">
					{LuLoaderCircle({ className: `animate-spin text-gray-500 w-8 h-8` })}
				</div>
			) : (
				<div className={`flex-container p-0 md:p-0`}>
					<div className="pro">
						<Premium
							unlockEssay={unlockEssay}
							closeParentModal={() => setOpen(false)}
							setLoginModal={setLoginModal}
							noMoreCredits={noMoreCredits}
						/>
					</div>
				</div>
			)}
		</Modal>
	)
}

export default Subscriptions
